import { useCanvasById } from "@/api/canvas";
import { useFlowSessionById } from "@/api/flow_sessions";
import { useWorkspaces } from "@/api/workspaces";
import { ROUTES } from "@/constants/routes";
import { cn } from "@/utils";
import { route } from "@/utils/route";
import { Icon as MetadataIconRenderer } from "@cohesive/ui-components";
import { Frame, Layers, MessagesSquare, Users } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";
import { BROWSER_LIST_COL } from "./browserListColumns";

/** Container-name lookups are decorative; a long stale window keeps the
 *  constant row remounts (sort flips, tree expands) from refetch storms —
 *  react-query already dedupes rows sharing a container to one request. */
const LOOKUP_STALE_MS = 5 * 60 * 1000;

/**
 * Location column cell (wide mode only, see `BROWSER_LIST_COL`): the item's
 * container as icon + name linking to its page. Most specific wins — the
 * chat a file was uploaded to, the canvas it lives on, then the owning
 * workspace. A workspace id outside the user's own list (the same rule as
 * the rows' shared-with-me glyph) renders as a "Shared with me" link to the
 * shared page instead. Items with no container relation (skills) render an
 * em dash; while a lookup is still loading the cell stays blank rather than
 * flash a wrong label.
 */
export function BrowserLocationCell({
  workspaceId,
  canvasId,
  flowSessionId,
}: {
  workspaceId?: string;
  canvasId?: string;
  flowSessionId?: string;
}) {
  const { data: workspaces } = useWorkspaces();
  // Both hooks no-op (enabled: false) without their id, so rows only fetch
  // the one container they actually sit in.
  const { data: canvas } = useCanvasById(canvasId, {
    staleTime: LOOKUP_STALE_MS,
  });
  const { data: flowSession } = useFlowSessionById(flowSessionId ?? "", {
    staleTime: LOOKUP_STALE_MS,
  });
  const workspace = workspaceId
    ? workspaces?.find((ws) => ws.id === workspaceId)
    : undefined;

  let content: React.ReactNode = "—";
  if (flowSessionId) {
    content = flowSession ? (
      <LocationLink
        to={route(ROUTES.CHAT.DETAIL, { flowSessionId })}
        label={flowSession.name?.trim() || "Untitled chat"}
        icon={<MessagesSquare className="size-3.5" />}
      />
    ) : null;
  } else if (canvasId) {
    content = canvas ? (
      <LocationLink
        to={route(ROUTES.CANVAS.DETAIL, { canvasId })}
        label={canvas.name || "Untitled"}
        icon={<Frame className="size-3.5" />}
      />
    ) : null;
  } else if (workspaceId && !workspaces) {
    content = null;
  } else if (workspace) {
    content = (
      <LocationLink
        to={route(ROUTES.WORKSPACES.DETAIL, { workspaceId: workspace.id })}
        label={workspace.is_private ? "My workspace" : workspace.name}
        icon={
          workspace.metadata?.icon ? (
            <MetadataIconRenderer icon={workspace.metadata.icon} size={14} />
          ) : (
            <Layers className="size-3.5" />
          )
        }
      />
    );
  } else if (workspaceId) {
    content = (
      <LocationLink
        to={ROUTES.SHARED.ROOT}
        label="Shared with me"
        icon={<Users className="size-3.5" />}
      />
    );
  }

  return (
    <span
      className={cn(
        BROWSER_LIST_COL.location,
        "truncate text-xs text-muted-foreground",
      )}
    >
      {content}
    </span>
  );
}

/** Underline-on-hover navigation, insulated from the row's own gestures:
 *  clicks (and Enter, and a stray double click) must follow the link, not
 *  select or open the row around it. */
function LocationLink({
  to,
  label,
  icon,
}: {
  to: string;
  label: string;
  icon: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      title={label}
      className="inline-flex max-w-full items-center gap-1 align-bottom hover:text-foreground hover:underline"
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <span className="inline-flex size-3.5 shrink-0 items-center justify-center">
        {icon}
      </span>
      <span className="truncate">{label}</span>
    </Link>
  );
}
